import moment from "moment-timezone"
import momentTimezone from "moment-timezone"
import { getMidnightOffset } from "../components/screens/settings/selectors"
import { getTimestampFilter } from "../components/filters/selectors"
import type { StateObject } from "../components/redux/types"
import { Moment } from "moment"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { IsoDate, IsoDateTime } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"

export const getHourOffseted = (hour: number, midnightOffset: number) =>
    hour >= midnightOffset / 60 ? hour : hour + 24

export const getCurrentHour = () => getHour(moment())

export const getHour = (date: Moment) => Number(date.format("H"))

export const isToFilterToday = (state: StateObject): boolean => {
    const filter = getTimestampFilter(state)
    return filter && isToday(moment(filter.to), getMidnightOffset(state))
}

export const isFromFilterToday = (state: StateObject): boolean => {
    const filter = getTimestampFilter(state)
    return filter && isToday(moment(filter.from), getMidnightOffset(state))
}

export const isDateFilterToday = (state: StateObject): boolean => isFromFilterToday(state) && isToFilterToday(state)

export const isSameDay = (date1: Moment, date2: Moment, midnightOffset: number): boolean =>
    date1.subtract(midnightOffset, "minutes").isSame(date2.subtract(midnightOffset, "minutes"), "day")

export const isToday = (date: Moment, midnightOffset: number): boolean => isSameDay(moment(), date, midnightOffset)

export const isMoreDaysThan = (
    days: number,
    range: {
        from: string
        to: string
    }
): boolean => Math.abs(moment(range.from).diff(moment(range.to), "days")) >= days

export const getUtcTimestamp = () => moment.utc().format()

export const getDisabledDatesForFuture = (date: moment) => date && date >= moment().add(1, "day").startOf("day")

export const getUtcStartOfDay = (
    date: string | moment,
    timezone: Maybe<string> = null,
    midnightOffset: number = 0
): IsoDateTime => {
    if (timezone) {
        const timezoneDate = momentTimezone.tz(date, timezone)
        return timezoneDate.clone().startOf("day").add(midnightOffset, "m").utc().format()
    } else {
        return moment(date).local().startOf("day").add(midnightOffset, "m").utc().format()
    }
}

export const getUtcEndOfDay = (
    date: string | moment,
    timezone: Maybe<string> = null,
    midnightOffset: number = 0
): IsoDateTime => {
    if (timezone) {
        const timezoneDate = momentTimezone.tz(date, timezone)
        return timezoneDate.clone().endOf("day").add(midnightOffset, "m").utc().format()
    } else {
        return moment(date).local().endOf("day").add(midnightOffset, "m").utc().format()
    }
}

export const getToday = (state: StateObject): Moment => {
    const midnightOffset = getMidnightOffset(state)
    return moment().local().subtract(midnightOffset, "m").startOf("day")
}

export const getDateFromUtc = (
    date: string | moment,
    timezone: Maybe<string> = null,
    midnightOffset: number = 0,
    format: string = ""
) => {
    if (timezone) {
        return momentTimezone(date).tz(timezone).subtract(midnightOffset, "m").format(format)
    } else {
        return moment(date).local().subtract(midnightOffset, "m").format(format)
    }
}

export const formatUtcTimestamp = (timestamp: Date | string, timezone: Maybe<string> = "America/New_York"): string =>
    momentTimezone(timestamp).tz(timezone).utc().format()

export const getDateString = (timestamp: Moment): string => timestamp.format("YYYY-MM-DD")

export const differenceInSeconds = (date1: IsoDateTime, date2: IsoDateTime) =>
    moment.duration(moment(date2).diff(date1)).asSeconds()

export const sortIsoDate = (
    a: Maybe<IsoDate>,
    b: Maybe<IsoDate>,
    options?: { direction?: "asc" | "desc"; nullFirst?: boolean }
) => {
    const direction = options?.direction ?? "asc"
    const nullFirst = options?.nullFirst ?? false

    if (a == null) {
        return nullFirst ? -1 : 1
    }
    if (b == null) {
        return nullFirst ? 1 : -1
    }
    return a.localeCompare(b) * (direction === "asc" ? 1 : -1)
}
